import { takeLatest, call, put, all } from 'redux-saga/effects';
import type { AllEffect, ForkEffect } from 'redux-saga/effects';
import { CloudActions, UserServiceUrls } from '@portal/portal-core';

import { UserClientUtils } from 'ducks/user-client';
import { GDPRActions } from 'actions';

// Minimal shape of the runtime config object threaded through the sagas.
// `getAuthToken` is forwarded to `UserClientUtils.sendAction`.
interface SagaConfig {
  baseApiUrl: string;
  getAuthToken?: () => string | null | undefined;
}

interface UpdatePersonalDataTrackingAction {
  type: typeof GDPRActions.UPDATE_PERSONAL_DATA_TRACKING;
  payload: {
    userId: string;
    optOutDate: string | null;
  };
}

// Shape of the response returned by UserClientUtils.sendAction (a `.js` boundary).
interface SendActionResult {
  failed?: boolean;
  succeeded?: boolean;
  error?: unknown;
}

export function* updatePersonalDataTracking(
  config: SagaConfig,
  action: UpdatePersonalDataTrackingAction,
): Generator<unknown, void, unknown> {
  try {
    const serverAction = CloudActions.updateUserTracking(
      action.payload.userId,
      action.payload.optOutDate,
    );
    const url = UserServiceUrls.trackingUrl(config.baseApiUrl, action.payload.userId);
    const {
      failed,
      succeeded,
      error: requestError,
    } = (yield call(UserClientUtils.sendAction, config, serverAction, url)) as SendActionResult;

    if (failed) {
      yield put(
        GDPRActions.updatePersonalDataTrackingResult({
          open: true,
          success: false,
          result:
            'Something went wrong updating your personal data preferences. Please retry the operation.',
          error: requestError,
        }),
      );
    } else if (succeeded) {
      yield put(
        GDPRActions.updatePersonalDataTrackingResult({
          open: true,
          success: true,
          result: 'Your personal data preferences have been updated.',
          error: requestError,
        }),
      );
    }
  } catch (error) {
    yield put(
      GDPRActions.updatePersonalDataTrackingResult({
        open: true,
        success: false,
        result:
          'Something went wrong updating your personal data preferences. Please retry the operation.',
        error,
      }),
    );
  }
}

/* istanbul ignore next */
export function* watchUpdatePersonalDataTracking(
  config: SagaConfig,
): Generator<ForkEffect, void, unknown> {
  yield takeLatest(GDPRActions.UPDATE_PERSONAL_DATA_TRACKING, updatePersonalDataTracking, config);
}

/* istanbul ignore next */
export default (config: SagaConfig = {} as SagaConfig) =>
  function* rootSaga(): Generator<AllEffect<unknown>, void, unknown> {
    yield all([watchUpdatePersonalDataTracking(config)]);
  };
